import { graphHooks } from '@tunasong/graph-lib/react'
import { useSelector } from '@tunasong/redux'
import type { Persisted, Poll } from '@tunasong/schemas'
import { isDatePoll, isGroupedPoll, isManyOfManyPoll, isOneOfManyPoll, isPersisted, isPoll } from '@tunasong/schemas'
import { Typography, useNavigateToEntity, Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useCallback } from 'react'
import { DatePollVote } from '../date-poll-vote.js'
import { GroupedPollVote } from '../grouped/vote.js'
import { ManyOfManyPollVote } from '../many-of-many/vote.js'
import { OneOfManyPollVote } from '../one-of-many/vote.js'
import type { PollViewProps } from './types.js'

const MainView: FC<PollViewProps> = props => {
  const { entity: poll } = props

  const updateEntity = graphHooks.useEntityUpdate({ debounceDelay: 0 })

  const userId = useSelector(state => state.user.userId)

  const handleChange = useCallback((update: Persisted<Poll>) => updateEntity(poll.id, update), [poll.id, updateEntity])

  const navigateToEntity = useNavigateToEntity()
  const navigateToEdit = () => navigateToEntity(poll, { layout: 'Edit' })

  if (!userId) {
    return null
  }

  // GH #1156: legacy Polls persisted before `name`/`pollType`/`pollOptions` became required
  // fail `isPoll(poll)` at runtime. The view used to `invariant(...)` on the result, throwing
  // to the React error boundary and reading as "content view is broken." Render a graceful
  // setup CTA instead — the user can fix the poll from the Edit layout. `isPersisted` is
  // expected to be true since the prop type is `Persisted<Poll>`, but we keep the guard so
  // a corrupted Redux state can't crash the page either.
  if (!isPersisted(poll) || !isPoll(poll)) {
    return (
      <Typography variant="body1">
        This poll needs to be set up. <Button onClick={navigateToEdit}>Set up the poll</Button>
      </Typography>
    )
  }

  if (poll.pollOptions.length === 0) {
    return (
      <Typography variant="body1">
        No poll options are defined. <Button onClick={navigateToEdit}>Set up the poll</Button>
      </Typography>
    )
  }

  /** A rehearsal poll is a many-of-many poll with dated slots, so this branch comes first. */
  if (isDatePoll(poll)) {
    return <DatePollVote poll={poll} userId={userId} />
  }
  /** A grouped poll is many-of-many too, so it is routed before the plain branch. */
  if (isGroupedPoll(poll)) {
    return <GroupedPollVote poll={poll} userId={userId} onChange={handleChange} sort={poll.randomizeOptions} />
  }
  if (isManyOfManyPoll(poll)) {
    return <ManyOfManyPollVote poll={poll} userId={userId} onChange={handleChange} sort={poll.randomizeOptions} />
  }
  if (isOneOfManyPoll(poll)) {
    return <OneOfManyPollVote poll={poll} userId={userId} onChange={handleChange} sort={poll.randomizeOptions} />
  }

  return null
}

export default MainView
